import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { AddonFeatureManager } from 'core';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { L1Object, L1ObjectObjective } from 'core/l1Object/L1Object';
import {
  DefaultL1ObjectManager,
  L1ObjectManager
} from 'core/l1Object/L1ObjectManager';
import { BidStrategy } from 'core/l2Object/L2Object';
import { Order } from 'core/order/Order';
import { RtbCampaignListBasic } from 'core/rtbCampaign/RtbCampaign';
import { getPriceValue } from 'helper/CurrencyHelper';
import { useCallAPI } from 'hooks/useCallAPI';
import i18n from 'i18n';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { useLocation } from 'react-router-dom';
import { validateEmpty, validateMinMax } from 'utils/ValidateUtils';
import {
  DefaultL1ObjectCreateFormChannelModel,
  L1ObjectCreateFormChannelModel
} from './L1ObjectCreateFormChannelModel';
import {
  DefaultL1ObjectEditFormModel,
  L1ObjectEditFormChannelModel
} from './L1ObjectEditFormChannelModel';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { ROUTE_PATH } from 'enum/RoutePath';
import { getSalesChannelName } from 'utils/I18nUtils';

const defaultL1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager();

type ChannelRelatedData = {
  totalBudget: number;
  validate: (values: Partial<L1Object>) => any;
  onCBOChange: (
    values: Partial<L1Object>,
    valueUpdater: (updateFunc: (prevValue: any) => any) => void,
    enable: boolean
  ) => void;
  showManagementModal: (
    values: Partial<L1Object>,
    valuesUpdater: (updateFunc: (prevValue: any) => any) => void
  ) => any;
  showRemainBudget: (values: Partial<L1Object>) => boolean;
  getCboChangeHint: (l1Object: Partial<L1Object>) => string | undefined;
  getDefaultBidStrategy: () => BidStrategy;
  save: (values: Partial<L1Object>) => Promise<void>;
};

export type L1ObjectFormData = {
  channelModel?: L1ObjectCreateFormChannelModel | L1ObjectEditFormChannelModel;
  formType: string;
  title: string;
  loading: boolean;
  initL1Object?: Partial<L1Object>;
  l1ObjectTypeOptions: SelectOptions[];
  objectiveOptions: SelectOptions[];
  redirectData?: {
    pathname: string;
    state?: any;
  };
  currentUrl: string;
  cancelTargetPath: string;
  order: Order;
  l2ObjectList: RtbCampaignListBasic[];
  canEditBidStrategy: boolean;
  budgetManagementModalData: any;
  breadcrumbs: any[];
  canEditViewableImpressInReportSetting: boolean;
  closeManagementModal: () => void;
  onChannelChange: (
    valueUpdater: (updateFunc: (prevValue: any) => any) => void,
    channel: string
  ) => void;
} & ChannelRelatedData;

const useL1ObjectTypeOptions = (addonFeatureManager: AddonFeatureManager) => {
  const l1ObjectTypeOptions = useMemo(() => {
    // ECOPD-5566：停用渠道不得出現在建立廣告活動群組的選項裡
    return ChannelGroupSetting.activeValues()
      .filter((setting: ChannelGroupSetting) => {
        return ADDONFEATURE.CHANNEL[setting.key]
          ? addonFeatureManager.isFeatureEnable(
              ADDONFEATURE.CHANNEL[setting.key]
            )
          : setting.config.id
            ? addonFeatureManager.isFeatureEnable(`${setting.config.id}`)
            : false;
      })
      .map((setting) => ({
        // ECOPD-5566: 走 salesChannels.<id>，停用渠道才會帶後綴（此清單已濾掉停用
        // 渠道，這裡是為了與其他渠道名稱顯示一致）
        label: getSalesChannelName(setting.config.id, setting.config.name),
        value: setting.key
      }));
  }, [addonFeatureManager]);
  return l1ObjectTypeOptions;
};

export const useCreateL1ObjectFormModel = (
  order: Order,
  addonFeatureManager: AddonFeatureManager,
  l1ObjectManager: L1ObjectManager = defaultL1ObjectManager
): L1ObjectFormData => {
  const allowedChannels =
    l1ObjectManager.getAllowedChannels(addonFeatureManager);
  const initL1ObjectChannel = !_.isEmpty(allowedChannels)
    ? allowedChannels[0]
    : ChannelGroupSetting.RTB.key;

  // Use useRef to persist initL1Object across renders
  const initL1ObjectRef = useRef({
    name: '',
    channel: initL1ObjectChannel,
    budget: 0,
    autoOptimise: false,
    budgetBalance: order.budgetBalance,
    objective: L1ObjectObjective.AWARENESS
  });

  const [channelType, setChannelType] = useState<string>(initL1ObjectChannel);

  const channelGroupId = useMemo(() => {
    return ChannelGroupSetting.fromKey(channelType).config.id;
  }, [channelType]);

  const canEditViewableImpressInReportSetting = useMemo(
    () => !l1ObjectManager.isOutdoorChannel(channelType),
    [channelType, l1ObjectManager]
  );

  const getChannelModel = useCallback(
    (channel: string) => {
      const useViewableImpressInReport = l1ObjectManager.isOutdoorChannel(
        channel
      )
        ? false
        : addonFeatureManager.isFeatureEnable(
            ADDONFEATURE.REPORT.REPORT_VIEWABLE_CTR
          );
      return new DefaultL1ObjectCreateFormChannelModel(
        order,
        useViewableImpressInReport
      );
    },
    [order, addonFeatureManager, l1ObjectManager]
  );

  let initChannelModel: L1ObjectCreateFormChannelModel = useMemo(() => {
    const model = getChannelModel(initL1ObjectChannel);
    model.initChannelData((updateFunc: (prevValue: any) => any) => {
      initL1ObjectRef.current = updateFunc(initL1ObjectRef.current);
    });
    return model;
  }, [initL1ObjectChannel, getChannelModel]);

  const { loading, callAPIs } = useCallAPI();
  const [redirectData, setRedirectData] = useState<
    | {
        pathname: string;
        state?: any;
      }
    | undefined
  >(undefined);

  const match = useRouteMatch();
  const currentUrl = match.url;
  const orderDetailPath = getRedirectPath(currentUrl);
  const l1ObjectTypeOptions = useL1ObjectTypeOptions(addonFeatureManager);
  const objectiveOptions = useMemo(() => {
    return defaultL1ObjectManager.getL1ObjectObjectiveOptions(channelType);
  }, [channelType]);

  const [channelModel, setChannelModel] =
    useState<L1ObjectCreateFormChannelModel>(initChannelModel);

  const onChannelChange = useCallback(
    (
      valueUpdater: (updateFunc: (prevValue: any) => any) => void,
      channel: string
    ) => {
      const channelModel = getChannelModel(channel);
      setChannelType(channel);
      setChannelModel(channelModel);
      channelModel.initChannelData(valueUpdater);
    },
    [getChannelModel]
  );

  useEffect(() => {
    callAPIs([async () => channelModel.init(_.noop)], () => {
      if (_.isEmpty(l1ObjectTypeOptions)) {
        const redirectPath = `/orders/${order.orderNumber}/campaign-groups/error404`;
        setRedirectData({
          pathname: redirectPath
        });
        return;
      }
    });
  }, [order, channelModel, callAPIs, l1ObjectTypeOptions]);

  const save = async (saveValue) => {
    const extraData = { order };
    const finalValue = {
      ...channelModel.toChannelNativeData(saveValue, extraData),
      channelGroupId
    };
    callAPIs(
      [l1ObjectManager.createL1Object.bind(l1ObjectManager, finalValue)],
      (l1ObjectId) => {
        setRedirectData({
          pathname: `${orderDetailPath}/campaign-groups/${l1ObjectId}`,
          state: {
            refreshOrder: shouldRefreshOrder(
              initL1ObjectRef.current,
              finalValue
            )
          }
        });
      }
    );
  };

  const validate = (value: Partial<L1Object>) => {
    const minBudget = channelModel.minBudget;
    const basicError = basicValidate(
      order,
      minBudget,
      _.defaultTo(_.get(order, 'budgetBalance'), 0),
      value
    );
    const channelDataError = channelModel.validate(value);
    return _.omitBy(
      {
        ...basicError,
        ...channelDataError
      },
      _.isEmpty
    );
  };

  const showRemainBudget = (values: Partial<L1Object>) => {
    return channelModel.showRemainBudget(values);
  };

  return {
    channelModel,
    formType: 'create',
    title: i18n.t('l1Object.labels.createTitle'),
    redirectData,
    loading,
    initL1Object: initL1ObjectRef.current,
    currentUrl,
    l1ObjectTypeOptions: l1ObjectTypeOptions,
    objectiveOptions,
    cancelTargetPath: orderDetailPath,
    order,
    l2ObjectList: [],
    canEditBidStrategy: true,
    totalBudget: _.defaultTo(_.get(order, 'budgetBalance'), 0),
    budgetManagementModalData: undefined,
    breadcrumbs: [
      {
        path: '/orders',
        breadcrumb: i18n.t('orderDetail.labels.title')
      },
      {
        path: '/orders/:orderNumber',
        breadcrumb: DynamicBreadcrumb,
        props: {
          label: _.get(order, 'projectName'),
          matchParam: 'orderNumber'
        }
      },
      {
        path: '/orders/:orderNumber/campaign-groups/new',
        breadcrumb: i18n.t('l1Object.labels.createTitle')
      }
    ],
    canEditViewableImpressInReportSetting,
    showManagementModal: () => {
      // This is intentional
    },
    closeManagementModal: () => {
      // This is intentional
    },
    validate,
    save,
    getCboChangeHint: channelModel.getCboChangeHint,
    getDefaultBidStrategy: channelModel.getDefaultBidStrategy,
    onChannelChange,
    onCBOChange: (
      values: Partial<L1Object>,
      valueUpdater: (updateFunc: (prevValue: any) => any) => void,
      enable: boolean
    ) => {
      channelModel.onCBOChange(enable, valueUpdater);
    },
    showRemainBudget
  };
};

const convertArrayToType = <T>(array: any[] | undefined): T[] | undefined => {
  if (!array) {
    return undefined;
  }

  return array as T[];
};

export const useEditL1ObjectFormModel = (
  order: Order,
  l1ObjectId: number,
  addonFeatureManager: AddonFeatureManager,
  initL2ObjectList?: RtbCampaignListBasic[],
  l1ObjectManager: L1ObjectManager = defaultL1ObjectManager
): L1ObjectFormData => {
  const { loading, callAPIs } = useCallAPI();
  const [redirectData, setRedirectData] = useState<
    | {
        pathname: string;
        state?: any;
      }
    | undefined
  >(undefined);
  const [initL1Object, setInitL1Object] = useState<L1Object | undefined>(
    undefined
  );
  const [l2ObjectList, setL2ObjectList] = useState<RtbCampaignListBasic[]>(
    _.defaultTo(initL2ObjectList, [])
  );
  const match = useRouteMatch();
  const currentUrl = match.url;
  const orderDetailPath = getRedirectPath(currentUrl);
  const location = useLocation();
  const cancelTargetPath = _.get(location.state, 'fromDetail', false)
    ? `${orderDetailPath}/campaign-groups/${l1ObjectId}`
    : orderDetailPath;

  const [budgetManagementModalData, setBudgetManagementModalData] =
    useState<any>();
  const [channelModel, setChannelModel] = useState<
    L1ObjectEditFormChannelModel | undefined
  >();

  const canEditViewableImpressInReportSetting = useMemo(() => {
    if (!initL1Object) {
      return false;
    }
    return !l1ObjectManager.isOutdoorChannel(initL1Object.channel);
  }, [initL1Object, l1ObjectManager]);

  const getL1ObjectFormModel = useCallback(
    (order, l1Object, initL2ObjectList) =>
      new DefaultL1ObjectEditFormModel(
        order,
        l1Object,
        convertArrayToType<RtbCampaignListBasic>(initL2ObjectList)
      ),
    []
  );

  const l1ObjectTypeOptions = useL1ObjectTypeOptions(addonFeatureManager);
  const objectiveOptions = useMemo(() => {
    const channel: string = _.get(
      initL1Object,
      'channel',
      ChannelGroupSetting.RTB.key
    );
    return defaultL1ObjectManager.getL1ObjectObjectiveOptions(channel);
  }, [initL1Object]);

  useEffect(() => {
    callAPIs(
      [l1ObjectManager.getL1Object.bind(l1ObjectManager, l1ObjectId)],
      (l1Object) => {
        const channelModel = getL1ObjectFormModel(
          order,
          l1Object,
          initL2ObjectList
        );
        setChannelModel(channelModel);
        setInitL1Object({
          ...l1Object,
          budget: _.defaultTo(l1Object.budget, 0)
        });
        // ECOPD-5566：停用渠道底下的群組轉唯讀。列表與詳情頁的編輯入口已由
        // `getModifyPermissionItem` 收掉，這裡擋的是直接打網址進來的情況。
        if (
          ChannelGroupSetting.isEffectivelyArchivedChannel(
            l1Object.channelGroupId
          )
        ) {
          setRedirectData({
            pathname: `/orders/${order.orderNumber}/campaign-groups/${l1Object.l1ObjectId}/edit/${ROUTE_PATH.ERROR403}`
          });
          return; // 後面的 API 不要再打，避免 L1ObjectForm unmount 後的 memory leak
        }
        if (
          l1Object.adsOrderId !== order.id ||
          !l1ObjectManager.isChannelAllowed(
            l1Object.channel,
            addonFeatureManager
          )
        ) {
          const redirectPath = `/orders/${order.orderNumber}/campaign-groups/${l1Object.l1ObjectId}/edit/error404`;
          setRedirectData({
            pathname: redirectPath
          });
          return; // to prevent the latter API calls and leads to memory leak when L1ObjectForm unmounted
        } else if (
          l1Object.objective === L1ObjectObjective.UNSPECIFIED ||
          l1Object.isSelfServe
        ) {
          const redirectPath = `/orders/${order.orderNumber}/campaign-groups/${l1Object.l1ObjectId}/edit/error403`;
          setRedirectData({
            pathname: redirectPath
          });
          return;
        }
        callAPIs([
          async () =>
            channelModel.init((data) => {
              data.l2ObjectList && setL2ObjectList(data.l2ObjectList);
            })
        ]);
      }
    );
  }, [
    l1ObjectId,
    l1ObjectManager,
    initL2ObjectList,
    order,
    getL1ObjectFormModel,
    addonFeatureManager,
    callAPIs
  ]);

  let channelModelActions: ChannelRelatedData = {
    totalBudget: 0,
    showManagementModal: _.noop,
    showRemainBudget: () => false,
    validate: _.noop,
    onCBOChange: _.noop,
    getCboChangeHint: () => undefined,
    getDefaultBidStrategy: () => BidStrategy.LOWEST_COST_WITHOUT_CAP,
    save: () => Promise.resolve()
  };

  if (channelModel) {
    const showManagementModal = (
      values: Partial<L1Object>,
      valuesUpdater: (updateFunc: (prevValue: any) => any) => void
    ) => {
      const modalData = channelModel.getBudgetManagementModalData(
        values,
        valuesUpdater
      );
      setBudgetManagementModalData({
        ..._.omit(modalData, 'onSubmit', 'onCancel'),
        submit: (budgetMap) => {
          modalData.onSubmit(budgetMap);
          setBudgetManagementModalData(undefined);
        },
        cancel: () => {
          modalData.onCancel();
          setBudgetManagementModalData(undefined);
        }
      });
    };

    const showRemainBudget = (values: Partial<L1Object>) => {
      return channelModel.showRemainBudget(values);
    };

    const validate = (values: Partial<L1Object>) => {
      const basicError = basicValidate(
        order,
        channelModel.minBudget(values),
        channelModel.totalBudget,
        values
      );
      const channelDataError = channelModel.validate(values);
      return _.omitBy(
        {
          ...basicError,
          ...channelDataError
        },
        _.isEmpty
      );
    };

    const onCBOChange = (
      values: Partial<L1Object>,
      valueUpdater: (updateFunc: (prevValue: any) => any) => void,
      enable: boolean
    ) => {
      channelModel.onCBOChange(
        enable,
        valueUpdater,
        _.partial(showManagementModal, values, valueUpdater)
      );
    };

    const getDefaultBidStrategy = () => {
      if (!initL1Object) {
        return BidStrategy.LOWEST_COST_WITHOUT_CAP;
      }

      return channelModel.getDefaultBidStrategy();
    };

    const save = async (saveValue) => {
      const extraData = { order };
      const finalValue = channelModel.toChannelNativeData(saveValue, extraData);
      callAPIs(
        [
          l1ObjectManager.updateL1Object.bind(
            l1ObjectManager,
            finalValue.l1ObjectId,
            finalValue
          )
        ],
        () => {
          setRedirectData({
            pathname: `${orderDetailPath}/campaign-groups/${finalValue.l1ObjectId}`,
            state: {
              refreshOrder: shouldRefreshOrder(initL1Object, finalValue)
            }
          });
        }
      );
    };

    channelModelActions = {
      totalBudget: channelModel.totalBudget,
      showManagementModal,
      showRemainBudget,
      validate,
      onCBOChange,
      getCboChangeHint: channelModel.getCboChangeHint,
      getDefaultBidStrategy,
      save
    };
  }

  return {
    channelModel,
    formType: 'edit',
    title: i18n.t('l1Object.labels.editTitle'),
    redirectData,
    loading,
    initL1Object: initL1Object,
    l1ObjectTypeOptions: l1ObjectTypeOptions,
    objectiveOptions,
    currentUrl,
    cancelTargetPath,
    order,
    l2ObjectList,
    canEditBidStrategy: false,
    budgetManagementModalData,
    breadcrumbs: [
      {
        path: '/orders',
        breadcrumb: i18n.t('orderDetail.labels.title')
      },
      {
        path: '/orders/:orderNumber',
        breadcrumb: DynamicBreadcrumb,
        props: {
          label: _.get(order, 'projectName'),
          matchParam: 'orderNumber'
        }
      },
      {
        path: '/orders/:orderNumber/campaign-groups/:l1ObjectId',
        breadcrumb: DynamicBreadcrumb,
        props: {
          label: _.get(initL1Object, 'name'),
          matchParam: 'l1ObjectId'
        }
      },
      {
        path: '/orders/:orderNumber/campaign-groups/:l1ObjectId/edit',
        breadcrumb: DynamicBreadcrumb,
        props: {
          prefix: i18n.t('common.labels.edit'),
          label: _.get(initL1Object, 'name'),
          matchParam: 'l1ObjectId'
        }
      }
    ],
    canEditViewableImpressInReportSetting,
    closeManagementModal: () => setBudgetManagementModalData(undefined),
    onChannelChange: () => {
      // This is intentional
    },
    ...channelModelActions
  };
};

function shouldRefreshOrder(initL1Object, finalL1Object) {
  let initBudget = _.get(initL1Object, 'budget', null);
  initBudget = initBudget === '' ? null : initBudget;
  return (
    initBudget !== finalL1Object.l1ObjectLifetimeBudget ||
    _.get(initL1Object, 'autoOptimise', false) !== finalL1Object.autoOptimise
  );
}

const basicValidate = (
  order: Order,
  minBudget: number,
  totalBudget: number,
  value: Partial<L1Object>
) => {
  const validateBudget = (budget) => {
    let error = validateEmpty(budget);
    if (error) {
      return error;
    }
    return validateMinMax(
      budget,
      getPriceValue(order.currency, minBudget),
      totalBudget
    );
  };
  return _.omitBy(
    {
      name: validateEmpty(value.name),
      budget: validateBudget(value.budget)
    },
    _.isEmpty
  );
};

const getRedirectPath = (currentUrl: string) => {
  const regMatch = currentUrl.match(/\/orders\/\d+\//);
  return regMatch ? regMatch[0].slice(0, -1) : '/';
};
